import { getReadyServiceWorkerRegistration, getServiceWorkerDiagnostics } from "./swRegistration";

function toUint8ArrayFromBase64(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) out[i] = rawData.charCodeAt(i);
  return out;
}

function makePushError(message, code, cause) {
  const err = new Error(message);
  err.code = code;
  if (cause) err.cause = cause;
  return err;
}

function assertPushPrerequisites() {
  const diag = getServiceWorkerDiagnostics();

  if (typeof window === "undefined") {
    throw makePushError("Push недоступны вне браузера", "NO_WINDOW");
  }
  if (!diag.secureContext) {
    throw makePushError("Push доступны только по HTTPS или на localhost", "INSECURE_CONTEXT");
  }
  if (!diag.supported) {
    throw makePushError("Service Worker не поддерживается этим браузером", "NO_SERVICE_WORKER");
  }
  if (!diag.enabledByEnv) {
    throw makePushError(
      "Service Worker отключён настройкой REACT_APP_ENABLE_SW=false или страница открыта не по HTTPS",
      "SERVICE_WORKER_DISABLED"
    );
  }
  if (diag.reactSnap) {
    throw makePushError("Push недоступны во время статической генерации страниц", "REACT_SNAP");
  }
  if (!("Notification" in window)) {
    throw makePushError("Notifications API не поддерживается этим браузером", "NO_NOTIFICATIONS_API");
  }
  if (!("PushManager" in window)) {
    throw makePushError("Push API недоступен в этом браузере, режиме или контексте", "NO_PUSH_MANAGER");
  }

  return diag;
}

export async function getCurrentPushSubscription() {
  const reg = await getReadyServiceWorkerRegistration();
  if (!reg?.pushManager) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

export async function subscribePush(vapidPublicKey) {
  const diag = assertPushPrerequisites();
  const reg = await getReadyServiceWorkerRegistration();

  if (!reg) {
    throw makePushError(
      `Service Worker не зарегистрирован. Проверь, что ${diag.url} открывается с HTTP 200 и лежит на frontend-домене`,
      "SERVICE_WORKER_UNAVAILABLE"
    );
  }
  if (!reg.pushManager) {
    throw makePushError("PushManager недоступен в текущей регистрации Service Worker", "REGISTRATION_NO_PUSH_MANAGER");
  }

  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  const cleanKey = String(vapidPublicKey || "").trim();
  if (!cleanKey) {
    throw makePushError("Публичный VAPID-ключ пустой", "EMPTY_VAPID_PUBLIC_KEY");
  }

  const options = {
    userVisibleOnly: true,
    applicationServerKey: toUint8ArrayFromBase64(cleanKey),
  };

  try {
    return await reg.pushManager.subscribe(options);
  } catch (err) {
    const name = String(err?.name || "");
    if (name === "NotAllowedError") {
      throw makePushError("Браузер запретил уведомления. Проверь разрешения сайта", "PUSH_PERMISSION_DENIED", err);
    }
    if (name === "NotSupportedError") {
      throw makePushError("Push API не поддерживается текущим браузером/режимом", "PUSH_NOT_SUPPORTED", err);
    }
    if (name === "AbortError") {
      throw makePushError("Браузер не смог создать Push-подписку. Проверь Service Worker и VAPID-ключ", "PUSH_ABORTED", err);
    }
    throw err;
  }
}

export async function unsubscribePush() {
  const sub = await getCurrentPushSubscription();
  if (!sub) return true;
  try {
    return await sub.unsubscribe();
  } catch {
    return false;
  }
}
